import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import type { Message } from '@/types/messages.ts';
import type { Transcript } from './useTranscript.ts';

/** How close to the top counts as reaching it, in pixels. */
const NEAR_TOP_PX = 100;

export interface TranscriptScroll {
  /** The transcript's own box; on a phone the page scrolls instead. */
  scroller: RefObject<HTMLDivElement | null>;
  scrollToEnd: () => void;
  /** Reads one older page, keeping the reader on the words they were looking at. */
  readOlder: () => void;
  onScroll: () => void;
}

/**
 * Scrolling through a transcript that is read backwards a page at a time: an
 * older page loads when the reader nears the top, and lands without moving
 * what is on screen.
 */
export function useTranscriptScroll(
  transcript: Transcript,
  shown: Message[],
  belowMobile: boolean,
): TranscriptScroll {
  const { loading, loadingMore, hasMore, loadMore, merged } = transcript;
  const scroller = useRef<HTMLDivElement>(null);
  /* The height the transcript had just before an older page was put on top of it. */
  const heightBeforeOlder = useRef<number | null>(null);
  const oldest = shown[0]?.id;

  /* On a phone the transcript is a screen of its own, so the page is what scrolls. */
  const scrollBox = useCallback(
    (): HTMLElement | null => (belowMobile ? document.documentElement : scroller.current),
    [belowMobile],
  );

  const scrollToEnd = useCallback(() => {
    const element = scrollBox();
    if (element) element.scrollTop = element.scrollHeight;
  }, [scrollBox]);

  const readOlder = useCallback(() => {
    const element = scrollBox();
    if (!element) return;

    heightBeforeOlder.current = element.scrollHeight;
    loadMore();
  }, [scrollBox, loadMore]);

  /*
   * An older page lands above what is on screen and would push it down by
   * exactly its own height. Putting that height back leaves the reader looking
   * at the same words.
   */
  useLayoutEffect(() => {
    const element = scrollBox();
    const before = heightBeforeOlder.current;
    if (!element || before === null) return;

    element.scrollTop += element.scrollHeight - before;
    heightBeforeOlder.current = null;
  }, [oldest, scrollBox]);

  /*
   * A channel whose newest conversation is three messages long leaves nothing
   * to scroll, and infinite scrolling never starts. Keep reading back until
   * there is.
   */
  useEffect(() => {
    if (!merged || !hasMore || loading || loadingMore) return;

    const element = scrollBox();
    if (!element || element.scrollHeight > element.clientHeight) return;

    /* Read as an older page: the reader stays at the end rather than at the top. */
    readOlder();
  }, [merged, hasMore, loading, loadingMore, readOlder, shown, scrollBox]);

  const onScroll = useCallback(() => {
    const transcriptBox = scroller.current;
    if (!transcriptBox || loadingMore || !hasMore) return;

    /* How far the reader is into the transcript, whichever box is scrolling it. */
    const readInto = belowMobile
      ? -transcriptBox.getBoundingClientRect().top
      : transcriptBox.scrollTop;
    if (readInto >= NEAR_TOP_PX) return;

    readOlder();
  }, [belowMobile, loadingMore, hasMore, readOlder]);

  useEffect(() => {
    if (!belowMobile) return;

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [belowMobile, onScroll]);

  return { scroller, scrollToEnd, readOlder, onScroll };
}
